import api from './api';

export type BookBoard = 'AP' | 'TELANGANA' | 'CBSE' | 'ICSE' | 'STATE' | 'OTHER';
export type BookCondition = 'NEW' | 'LIKE_NEW' | 'USED' | 'OLD';
export type BookCategory = 'TEXTBOOK' | 'GUIDE' | 'COMPETITIVE_EXAM' | 'REFERENCE' | 'STORY' | 'STATIONERY' | 'OTHER';
export type ListingType = 'SELL' | 'EXCHANGE' | 'DONATE';
export type PreferredCondition = 'NEW' | 'USED' | 'ANY';
export type ShopAvailability = 'AVAILABLE' | 'ARRANGE_IN_1_DAY' | 'ARRANGE_IN_2_3_DAYS' | 'NOT_AVAILABLE';
export type ShopProductType =
  | 'NEW_BOOK'
  | 'USED_BOOK'
  | 'GUIDE'
  | 'COMPETITIVE_EXAM_BOOK'
  | 'REFERENCE_BOOK'
  | 'STORY_BOOK'
  | 'NOTEBOOK'
  | 'STATIONERY'
  | 'SCHOOL_BAG'
  | 'OTHER';

export interface BookLocation {
  area: string;
  villageOrTown?: string;
  mandal?: string;
  district?: string;
  state?: string;
  pincode?: string;
}

export interface BookListingPayload {
  sellerType?: 'STUDENT' | 'PARENT' | 'SHOP';
  title: string;
  classOrCourse?: string;
  board?: BookBoard;
  subject?: string;
  category: BookCategory;
  condition: BookCondition;
  originalPrice?: number;
  sellingPrice?: number;
  listingType: ListingType;
  description?: string;
  photos?: string[];
  location?: BookLocation;
}

export interface BookRequestPayload {
  requestedTitle: string;
  classOrCourse?: string;
  board?: BookBoard;
  subject?: string;
  examCategory?: string;
  quantity?: number;
  preferredCondition?: PreferredCondition;
  expectedPrice?: number;
  location?: BookLocation;
}

export interface DonationPayload {
  title: string;
  classOrCourse?: string;
  board?: string;
  subject?: string;
  condition: Exclude<BookCondition, 'NEW'>;
  photos?: string[];
  pickupRequired?: boolean;
  location?: BookLocation;
}

export interface BookShopPayload {
  shopName: string;
  ownerName: string;
  phone: string;
  alternatePhone?: string;
  email?: string;
  address: string;
  area: string;
  villageOrTown?: string;
  mandal?: string;
  district: string;
  state: string;
  pincode: string;
  availableBoards?: BookBoard[];
  supportedClasses?: string[];
  categories?: string[];
  offersNewBooks?: boolean;
  offersUsedBooks?: boolean;
  offersStationery?: boolean;
  offersHomeDelivery?: boolean;
  buysUsedBooks?: boolean;
  acceptsBookRequests?: boolean;
  openingTime?: string;
  closingTime?: string;
  weeklyHoliday?: string;
  shopNameProofUrl?: string;
  ownerIdProofUrl?: string;
  addressProofUrl?: string;
  shopFrontPhotoUrl?: string;
  gstNumber?: string;
}

export interface ShopProductPayload {
  shopId: string;
  title: string;
  productType: ShopProductType;
  board?: BookBoard;
  classOrCourse?: string;
  subject?: string;
  examCategory?: string;
  condition?: BookCondition;
  price?: number;
  discountPrice?: number;
  stockStatus?: 'AVAILABLE' | 'LIMITED' | 'OUT_OF_STOCK';
  photos?: string[];
  description?: string;
}

export interface BuybackOfferPayload {
  listingId: string;
  shopId: string;
  offeredPrice: number;
  message?: string;
  pickupAvailable?: boolean;
}

export interface ShopResponsePayload {
  shopId: string;
  availability: ShopAvailability;
  price?: number;
  message?: string;
  homeDeliveryAvailable?: boolean;
}

export const booksService = {
  search: async (params?: Record<string, string>) => {
    const response = await api.get('/books/search', { params });
    return response.data;
  },
  listListings: async (params?: Record<string, string>) => {
    const response = await api.get('/books/listings', { params });
    return response.data;
  },
  getListing: async (id: string) => {
    const response = await api.get(`/books/listings/${id}`);
    return response.data;
  },
  createListing: async (payload: BookListingPayload) => {
    const response = await api.post('/books/listings', payload);
    return response.data;
  },
  myListings: async () => {
    const response = await api.get('/my-book-listings');
    return response.data;
  },
  createRequest: async (payload: BookRequestPayload) => {
    const response = await api.post('/books/requests', payload);
    return response.data;
  },
  myRequests: async () => {
    const response = await api.get('/books/requests/my');
    return response.data;
  },
  createDonation: async (payload: DonationPayload) => {
    const response = await api.post('/books/donations', payload);
    return response.data;
  },
  listDonations: async () => {
    const response = await api.get('/books/donations');
    return response.data;
  },
  listBookShops: async (params?: Record<string, string>) => {
    const response = await api.get('/book-shops', { params });
    return response.data;
  },
  getBookShop: async (id: string) => {
    const response = await api.get(`/book-shops/${id}`);
    return response.data;
  },
  registerBookShop: async (payload: BookShopPayload) => {
    const response = await api.post('/book-shops/register', payload);
    return response.data;
  },
  sendShopEnquiry: async (shopId: string, payload: BookRequestPayload) => {
    const response = await api.post(`/book-shops/${shopId}/enquiry`, payload);
    return response.data;
  },
  myBookShops: async () => {
    const response = await api.get('/book-shops/me');
    return response.data;
  },
  myShopProducts: async () => {
    const response = await api.get('/book-shops/products/my');
    return response.data;
  },
  createShopProduct: async (payload: ShopProductPayload) => {
    const response = await api.post('/book-shops/products', payload);
    return response.data;
  },
  shopRequests: async () => {
    const response = await api.get('/book-shops/requests');
    return response.data;
  },
  respondToShopRequest: async (requestId: string, payload: ShopResponsePayload) => {
    const response = await api.post(`/book-shops/requests/${requestId}/respond`, payload);
    return response.data;
  },
  createBuybackOffer: async (payload: BuybackOfferPayload) => {
    const response = await api.post('/book-shops/buyback-offers', payload);
    return response.data;
  },
  shopBuybackOffers: async () => {
    const response = await api.get('/book-shops/buyback-offers');
    return response.data;
  },
  adminOverview: async () => {
    const response = await api.get('/admin/books/overview');
    return response.data;
  },
  adminBookShops: async () => {
    const response = await api.get('/admin/book-shops');
    return response.data;
  },
  approveBookShop: async (id: string) => {
    const response = await api.post(`/admin/book-shops/${id}/approve`);
    return response.data;
  },
  rejectBookShop: async (id: string, rejectionReason?: string) => {
    const response = await api.post(`/admin/book-shops/${id}/reject`, { rejectionReason });
    return response.data;
  },
};

export default booksService;
